import { Box } from '@mui/material';
import { CircleCheck, CircleDot, CircleSlash, Layers } from 'lucide-react';
import type { ReactElement } from 'react';

import type { AdminSyncConflictCounts } from '@/admin/adminApi';
import { Metric } from '@/ui/kit';

/**
 * Les quatre compteurs du registre (ADR 0039, decision D6). Chaque nombre est
 * celui du serveur, **jamais deduit de la page affichee**, et **chacun a sa
 * date** — l'ecran l'ecrit sous chaque nombre :
 *
 * - **Detectes** : conflits detectes dans la periode ;
 * - **Ouverts** : a l'instant de la lecture, **sans periode** — un conflit ouvert
 *   l'est jusqu'a sa resolution, quelle que soit la date de sa detection. Comprend
 *   les conflits renvoyes a l'utilisatrice ;
 * - **Resolus** : resolus dans la periode, a leur date de resolution (classes
 *   compris : le suivi est ferme, ce qui ne dit pas qu'une saisie a ete conservee) ;
 * - **Sans objet** : constates dans la periode, a leur date de resolution.
 *
 * **Ils ne forment pas une partition** : un conflit detecte avant la periode et
 * resolu dedans est compte parmi les resolus, pas parmi les detectes. Ils suivent
 * la periode, la nature, le type d'enregistrement et le compte, **pas l'etat**.
 * Sans periode, ils se lisent sur toute la vie du registre.
 */
export const ConflictCounters = ({ counts, period }: { readonly counts: AdminSyncConflictCounts; readonly period: string }): ReactElement => (
  <Box component="section" aria-label="Compteurs des conflits" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
    <Metric label="Détectés" icon={Layers} value={counts.detected} caption={`détectés ${period}, un par saisie refusée`} />
    <Metric label="Ouverts maintenant" icon={CircleDot} value={counts.open} caption="à cet instant, quelle que soit la date de détection ; renvois à l’utilisatrice compris" />
    <Metric label="Résolus" icon={CircleCheck} value={counts.resolved} caption={`tranchés ou classés ${period}, à leur date de résolution`} />
    <Metric label="Sans objet" icon={CircleSlash} value={counts.notApplicable} caption={`constatés ${period}, à leur date de résolution`} />
  </Box>
);
